import { Stripe } from "@stripe/stripe-js";
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
} from "react";
import {
  AchProcessorType,
  ACH_METHODS,
  CardProcessorType,
  PaymentMethods,
  PAYMENTS_METHODS,
} from "../const";
import {
  PaymentContextValue,
  usePaymentContext,
} from "../contexts/payment-context";
import { setInternalContextState } from "../contexts/payment-context/state";
import { AchBankProcessor } from "./payment-processors/ach-bank-processor";
import { PaymentusIframe } from "./payment-processors/paymentus-iframe";
import { PaymentusPreloadElements } from "./payment-processors/paymentus-iframe/preload-elements";
import { PaypalProcessor } from "./payment-processors/paypal-processor";
import { StripeCardProcessor } from "./payment-processors/stripe-card-processor";
import {
  PaymentProcessorClientInternalRef,
  ProcessPayment,
  ProcessPaymentACHRequestData,
  ProcessPaymentPaymentusRequestData,
  ProcessPaymentPaypalRequestData,
  ProcessPaymentRequestData,
  ProcessPaymentStripeRequestData,
} from "./payment-processors/types";
import { PaymentsUI, PaymentsUIProps } from "./payments-ui";
import {
  ErrorBoundaryComponent,
  PaypalProcessorInternalView,
  PaypalProcessorScriptProviderView,
  PayPalApprovedNotification,
} from "./payment-processors/paypal-processor/view-components";
import { LoadingSpinner } from "./payment-processors/paypal-processor/view-components/loading-spinner/loading-spinner";
import { ErrorNotification } from "./error-notification";

export type PaymentComponentState = {
  activePaymentMethod?: PaymentMethods;
  cardProcessorResponse?: Record<string, unknown>;
  bankProcessorResponse?: Record<string, unknown>;
};

export type PaymentsComponentOwnProps = {
  // ------------------- Business Logic Related Value Props -------------------
  /**
   * @deprecated
   * When using this component you can set what payment processor will be used for
   * the card payments
   */
  cardPaymentProcessor?: CardProcessorType;
  achPaymentProcessor?: AchProcessorType;
  // ------------------- Business Logic Related Event Props -------------------
};

export type PaymentsComponentToIQUIPassthroughProps = Pick<
  PaymentsUIProps,
  | "className"
  | "bankProcessorIcon"
  | "cardProcessorIcon"
  | "paypalProcessorIcon"
  | "paymentMethodContainerClassName"
>;

export type PaymentsComponentProps = PaymentsComponentOwnProps &
  PaymentsComponentToIQUIPassthroughProps;

export type PaymentsComponentExposedApi = {
  processPayment: ProcessPayment<ProcessPaymentRequestData>;
  getClient: (
    paymentMethod: PaymentMethods
  ) => Record<string, unknown> | undefined;
  getCurrentActivePaymentMethod: () => PaymentMethods | undefined;
  setOpenPaymentMethod: (
    activePaymentMethod?: PaymentMethods
  ) => void | undefined;
};

export type ApplicationStateType = Pick<
  PaymentContextValue,
  | "token"
  | "baseURL"
  | "hostJsClient"
  | "onFailure"
  | "onResponse"
  | "onSuccess"
  | "applicationName"
  | "owner"
  | "state"
  | "dispatch"
  | "paymentusIframeBaseUrl"
  | "formsSetup"
  | "googleAnalytics"
  | "testIdName"
  | "styleType"
  | "customerId"
  | "updateDefaultPaymentMethod"
  | "setWalletCheckboxProps"
  | "brand"
  | "subbrand"
>;

export const PaymentsComponent = forwardRef<
  PaymentsComponentExposedApi,
  PaymentsComponentProps
>(
  (
    {
      // IQ UI passthrough props
      className,
      paymentMethodContainerClassName,
      bankProcessorIcon,
      cardProcessorIcon,
      paypalProcessorIcon,
      achPaymentProcessor,
    },
    ref
  ) => {
    const achBankProcessorRef = useRef<
      PaymentProcessorClientInternalRef<ProcessPaymentACHRequestData>
    >(null);
    const paymentusProcessorRef = useRef<
      PaymentProcessorClientInternalRef<ProcessPaymentPaymentusRequestData>
    >(null);
    const stripeProcessorRef = useRef<
      PaymentProcessorClientInternalRef<ProcessPaymentStripeRequestData, Stripe>
    >(null);
    const paypalProcessorRef = useRef<
      PaymentProcessorClientInternalRef<ProcessPaymentPaypalRequestData>
    >(null);

    /**
     * Internal state
     */
    const applicationState = usePaymentContext();
    const { state, dispatch } = applicationState;

    const handlePaymentTypeChange = useCallback(
      (activePaymentMethod: PaymentMethods) => {
        dispatch(
          setInternalContextState({
            ...state.internalState,
            activePaymentMethod: activePaymentMethod,
          })
        );
      },
      [state.internalState, dispatch]
    );

    useImperativeHandle(ref, () => ({
      processPayment: (requestData) => {
        switch (state.internalState?.activePaymentMethod) {
          case PAYMENTS_METHODS.CREDIT_CARD:
            return stripeProcessorRef.current?.processPayment(
              requestData as ProcessPaymentStripeRequestData
            );

          case PAYMENTS_METHODS.BANK_ACCOUNT:
            if (achPaymentProcessor === ACH_METHODS.ACH_PAYMENTUS) {
              return paymentusProcessorRef.current?.processPayment(
                requestData as ProcessPaymentPaymentusRequestData
              );
            } else {
              return achBankProcessorRef.current?.processPayment(
                requestData as ProcessPaymentACHRequestData
              );
            }

          case PAYMENTS_METHODS.PAYPAL:
            return paypalProcessorRef.current?.processPayment(
              requestData as ProcessPaymentPaypalRequestData
            );
          default:
            return achBankProcessorRef.current?.processPayment(
              requestData as ProcessPaymentACHRequestData
            );
        }
      },
      getClient: () => {
        return achBankProcessorRef.current?.getClient();
      },
      getCurrentActivePaymentMethod: () =>
        state.internalState?.activePaymentMethod,
      setOpenPaymentMethod: (activePaymentMethod?: PaymentMethods) => {
        dispatch(
          setInternalContextState({
            ...state.internalState,
            activePaymentMethod,
          })
        );
      },
    }));
    return (
      <PaymentsUI
        // passthrough props
        className={className}
        handlePaymentTypeChange={handlePaymentTypeChange}
        bankProcessorIcon={bankProcessorIcon}
        cardProcessorIcon={cardProcessorIcon}
        paypalProcessorIcon={paypalProcessorIcon}
        paymentMethodContainerClassName={paymentMethodContainerClassName}
        // props based by internal state
        internalState={state.internalState}
        cardProcessor={
          <StripeCardProcessor
            data-testid={`Stripe_Card_Processor`}
            ref={stripeProcessorRef}
            applicationState={applicationState}
          />
        }
        bankProcessor={
          achPaymentProcessor === ACH_METHODS.ACH_PAYMENTUS ? (
            <PaymentusIframe
              ref={paymentusProcessorRef}
              data-testid={`Paymentus_Bank_Processor`}
              applicationState={applicationState}
            />
          ) : (
            <AchBankProcessor
              data-testid={`Ach_Bank_Processor`}
              config={{}}
              ref={achBankProcessorRef}
            />
          )
        }
        paypalProcessor={
          <PaypalProcessor
            data-testid={`Paypal_Processor`}
            ref={paypalProcessorRef}
            applicationState={applicationState}
            components={{
              ErrorNotification: ErrorNotification,
              ErrorBoundaryComponent: ErrorBoundaryComponent,
              LoadingSpinner: LoadingSpinner,
              PaypalProcessorInternalView: PaypalProcessorInternalView,
              PaypalProcessorScriptProviderView: PaypalProcessorScriptProviderView,
              PayPalApprovedNotification: PayPalApprovedNotification,
            }}
          />
        }
        achPreloadElement={
          achPaymentProcessor === ACH_METHODS.ACH_PAYMENTUS ? (
            <PaymentusPreloadElements />
          ) : undefined
        }
      />
    );
  }
);

PaymentsComponent.displayName = "PaymentsComponent";
